import { useState, useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Container,
  Snackbar,
  Alert,
  Fade,
  CircularProgress,
  useTheme,
  DialogContentText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion"; // Importar framer-motion para animaciones
import { makeRequest } from "../../../assets/functions_helper";
import LogoLogo from "../../../assets/images/Logo_Horizontal.png"; // Asegúrate que estas rutas son correctas
import { SendButton, CloseButton } from "../../../components/buttons"; // Asumo que SendButton es un componente personalizado
import { NoSuggestionsTextField } from "../../../components/textFields"; // Este se mantiene como está en su lógica

import LoginGoogle from "../../loginGoogle";
import CompletationInfo from "./completationInfo";
import VerificationCode from "./verificationCode";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../components/customDialog";

// Función auxiliar para convertir HEX a RGBA
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Register = ({ setIsLoggedIn }) => {
  const methods = useForm();
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const [openMoreInfo, setOpenMoreInfo] = useState(false);
  const [dataFromGoogle, setDataFromGoogle] = useState(false);
  const [isCodeVerification, setIscodeVerification] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [dataResendCode, setDataResendCode] = useState(false);
  // Estado para mostrar pantalla de éxito tras el registro por email
  const [registrationComplete, setRegistrationComplete] = useState(false);

  useEffect(() => {
    let interval;
    if (isCountdownActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsCountdownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCountdownActive, countdown]);

  const setLocalStarageUserAndNavigate = (response) => {
    localStorage.setItem("token", response.access_token);
    localStorage.setItem("tokenType", response.token_type);
    const userRoles = response.user.roles;
    const userId = response.user.id;

    let minRoleId = 0;
    if (userRoles.length > 0) {
      minRoleId = userRoles.reduce(
        (minId, currentRole) => Math.min(minId, currentRole.id),
        userRoles[0].id
      );
    }

    localStorage.setItem("rol", minRoleId);
    localStorage.setItem("userId", userId);
    localStorage.setItem("name", response.user.name);

    if(setIsLoggedIn) setIsLoggedIn(true);
    navigate("/");
  };

  const handleFormSubmit = (data) => {
    if (isCodeVerification) {
      onSubmitVerificationCode(data);
    } else {
      setIscodeVerification(true);
      setDataResendCode(data);
      sentRequestVerificationCode(data);
    }
  };

  const sentRequestVerificationCode = (data) => {
    makeRequest(
      "POST",
      "restaurant_admin/send_cellphone_verification",
      {
        cellphone_number: `${data.phoneNumber}`,
        country_id: data.countryId,
      },
      "application/json",
      dataFromGoogle.access_token,
      dataFromGoogle.token_type,
      () => {
        navigate("/login");
      }
    )
      .then(() => {
        setCountdown(30);
        setIsCountdownActive(true);
      })
      .catch((error) => {
        if (error.error_code === "0009") {
          let waitTime = 30;
          if (error.time_remaining) {
            waitTime = error.time_remaining;
          } else if (error.message) {
            const match = error.message.match(/Wait for (\d+\.?\d*) seconds/);
            if (match) waitTime = parseFloat(match[1]);
          }
          const formattedWaitTime = Math.ceil(waitTime);
          setSnackbarMessage(`Por favor espera ${formattedWaitTime} segundos antes de volver a generar un nuevo código`);
          setSnackbarSeverity("warning");
          setCountdown(formattedWaitTime);
          setIsCountdownActive(true);
        } else if (error.error_code === "0003") {
          setSnackbarMessage("El número de teléfono ya se encuentra registrado");
          setSnackbarSeverity("error");
        } else {
          setSnackbarMessage(`Error al enviar el código de verificación`);
          setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
      });
  };

  const sentRequestToUpdatedInfo = (data) => {
    makeRequest(
      "POST",
      "restaurant_admin/update_info",
      {
        cellphone_number: `${data.phoneNumber}`,
        country_id: data.countryId,
      },
      "application/json",
      dataFromGoogle.access_token,
      dataFromGoogle.token_type,
      () => {
        navigate("/login");
      }
    )
      .then(() => {
        // Inyectamos el país seleccionado para que el frontend lo tome como país principal para pagos
        const updatedDataFromGoogle = {
          ...dataFromGoogle,
          user: {
            ...dataFromGoogle.user,
            country: { id: data.countryId },
          },
        };
        setLocalStarageUserAndNavigate(updatedDataFromGoogle);
      })
      .catch(() => {
        setSnackbarMessage(`Ocurrio un error al actualizar su informacion`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const onSubmitVerificationCode = (data) => {
    makeRequest(
      "POST",
      `restaurant_admin/verify_cellphone_code/${data.verificationCode}`,
      {},
      "application/json",
      dataFromGoogle.access_token,
      dataFromGoogle.token_type,
      () => {
        navigate("/login");
      }
    )
      .then(() => {
        sentRequestToUpdatedInfo(data);
      })
      .catch(() => {
        setSnackbarMessage(`Ocurrio un error al verificar el codigo`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const generateTokenEatery = (token) => {
    makeRequest(
      "POST",
      "restaurant_admin/google_authentication",
      token,
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      () => {}
    )
      .then((response) => {
        setDataFromGoogle(response);
        if (response.needs_registration === true) {
          setOpenMoreInfo(true);
        } else {
          setLocalStarageUserAndNavigate(response);
        }
      })
      .catch((error) => {
        console.error("Google auth error:", error);
        let errorCode = null;
        let errorMessage = "";

        if (error.error_code) {
          errorCode = error.error_code;
          errorMessage = error.message || "";
        } else if (error.detail && typeof error.detail === "string") {
          try {
            const parsed = JSON.parse(error.detail);
            errorCode = parsed.error_code;
            errorMessage = parsed.message;
          } catch (e) {
            const match = error.detail.match(/'error_code': '(\d+)'/);
            if (match) errorCode = match[1];
            const msgMatch = error.detail.match(/'message': '([^']+)'/);
            if (msgMatch) errorMessage = msgMatch[1];
          }
        } else if (typeof error === "string") {
          try {
            const parsed = JSON.parse(error);
            errorCode = parsed.error_code;
            errorMessage = parsed.message;
          } catch (e) {
            const match = error.match(/'error_code':\s*'([^']+)'/);
            if (match) errorCode = match[1];
            const msgMatch = error.match(/'message':\s*'([^']+)'/);
            if (msgMatch) errorMessage = msgMatch[1];
          }
        }

        if (errorCode === "0002" || errorMessage.includes("Email already registered")) {
          setSnackbarMessage("El correo electrónico ya está registrado. Por favor, inicia sesión con tu cuenta.");
          setSnackbarSeverity("warning");
        } else {
          setSnackbarMessage(`Ocurrió un error al intentar autenticarse con Google`);
          setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
      });
  };

  // --- PALETA DE COLORES CLARA Y VIBRANTE (Consistente con login.js) ---
  const customThemeColors = {
    // Colores de base Claros/Vibrantes
    ACCENT_PRIMARY: "#FF4747", // Rojo brillante para acción principal (SendButton)
    ACCENT_SECONDARY: "#5A4E99", // Morado de énfasis para enlaces/bordes
    TEXT_PRIMARY: "#212121", // Texto muy oscuro
    TEXT_SECONDARY: "#6A6A6A", // Texto secundario gris
    
    // Fondo de página animado (Claro)
    BACKGROUND_START: "#E0F7FA", // Cian/Azul Claro
    BACKGROUND_END: "#D1C4E9", // Lila suave
    
    // Fondo del formulario
    CONTAINER_BG: "#FFFFFF", // Fondo del formulario blanco puro
    
    // Colores de Estado
    error: "rgba(255, 71, 71, 0.9)", // Rojo de acento para error
    success: "rgba(76, 175, 80, 0.9)", 
    
    SHADOW: "rgba(90, 78, 153, 0.3)", // Sombra basada en el Morado
  };

  const onSubmit = (data) => {
    setLoading(true); // Iniciar carga
    makeRequest(
      "POST",
      `restaurant_admin/register_user/?country_id=${1}`,
      {
        name: data.name,
        email: data.email,
        last_name: data.last_name,
      },
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      () => {}
    )
      .then(() => {
        setRegistrationComplete(true);
        methods.reset();
      })
      .catch((error) => {
        console.error("Error en el registro:", error);
        if (error?.detail?.error_code === "0002") {
          setSnackbarMessage(
            "Este correo ya está registrado. Por favor, utiliza uno diferente."
          );
        } else {
          setSnackbarMessage(
            error?.detail?.message || "Ocurrió un error al completar la información."
          );
        }
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setLoading(false); // Finalizar carga
      });
  };

  const sendButtonStyles = {
    // Usar el rojo vibrante como primario
    backgroundColor: customThemeColors.ACCENT_PRIMARY,
    color: customThemeColors.CONTAINER_BG, // Texto blanco
    borderRadius: "12px",
    padding: theme.spacing(1.5, 4),
    fontWeight: "bold",
    fontSize: "0.95rem",
    textTransform: "none",
    transition: "background-color 0.2s ease-in-out, border-color 0.2s ease-in-out",
    border: `1px solid ${customThemeColors.ACCENT_PRIMARY}`,
    "&:hover": {
      backgroundColor: customThemeColors.ACCENT_PRIMARY,
      opacity: 0.9,
      boxShadow: `0 4px 15px ${hexToRgba(customThemeColors.ACCENT_PRIMARY, 0.4)}`, // Sombra al hover
    },
    "&:disabled": {
      backgroundColor: hexToRgba(customThemeColors.ACCENT_PRIMARY, 0.3),
      color: hexToRgba(customThemeColors.CONTAINER_BG, 0.5),
      borderColor: hexToRgba(customThemeColors.ACCENT_PRIMARY, 0.2),
    },
  };

  // Pantalla de éxito: se muestra después de enviar el formulario de registro por email
  if (registrationComplete) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          background: `linear-gradient(135deg, ${customThemeColors.BACKGROUND_START} 0%, ${customThemeColors.BACKGROUND_END} 100%)`,
          animation: "light-gradient-shift 20s ease infinite",
          "@keyframes light-gradient-shift": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
          backgroundSize: "200% 200%",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={8}
            sx={{
              padding: { xs: theme.spacing(4), sm: theme.spacing(6) },
              borderRadius: "20px",
              boxShadow: `0px 10px 40px rgba(90, 78, 153, 0.3)`,
              backgroundColor: customThemeColors.CONTAINER_BG,
              border: `2px solid rgba(90, 78, 153, 0.2)`,
              textAlign: "center",
              color: customThemeColors.TEXT_PRIMARY,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", mb: 2, color: customThemeColors.TEXT_PRIMARY }}
            >
              🎉 ¡Registro Enviado!
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: customThemeColors.TEXT_SECONDARY, mb: 1 }}
            >
              Se ha enviado un correo de verificación a tu dirección de email.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: customThemeColors.TEXT_SECONDARY, mb: 4 }}
            >
              Revisa tu bandeja de entrada y haz clic en el enlace para completar tu
              registro. Una vez verificado podrás crear tu contraseña e iniciar sesión.
            </Typography>
            <SendButton
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate("/login")}
              sx={sendButtonStyles}
            >
              Ir al Login
            </SendButton>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <CustomDialog
        open={openMoreInfo}
        onClose={() => {
          setOpenMoreInfo(!openMoreInfo);
          setIscodeVerification(false);
          setCountdown(0);
          setIsCountdownActive(false);
        }}
      >
        <CustomDialogTitle>Finaliza Registro</CustomDialogTitle>
        <CustomDialogContent>
          <DialogContentText sx={{ textAlign: "center", mb: 2 }}>
            {isCodeVerification ? (
              <>
                Ingresa el código enviado a tu celular
                {countdown > 0 && (
                  <Box mt={1} sx={{ color: "warning.main" }}>
                    Podrás reenviar el código en: {countdown}s
                  </Box>
                )}
              </>
            ) : (
              "Diligencia todos los campos para finalizar tu registro"
            )}
          </DialogContentText>

          <FormProvider {...methods}>
            {isCodeVerification ? <VerificationCode /> : <CompletationInfo />}
          </FormProvider>
        </CustomDialogContent>

        <CustomDialogActions>
          <CloseButton
            onClick={() => {
              setOpenMoreInfo(!openMoreInfo);
              setIscodeVerification(false);
              setCountdown(0);
              setIsCountdownActive(false);
            }}
            sx={{
              backgroundColor: "#E0E0E0",
              color: "#424242",
              "&:hover": {
                backgroundColor: "#D5D5D5",
              },
            }}
          >
            Cerrar
          </CloseButton>
          <SendButton onClick={methods.handleSubmit(handleFormSubmit)}>
            Enviar
          </SendButton>
          {isCodeVerification && (
            <SendButton
              variant="outlined"
              color="secondary"
              disabled={isCountdownActive}
              onClick={() => {
                methods.clearErrors();
                sentRequestVerificationCode(dataResendCode);
              }}
              sx={{
                position: "relative",
                "&:disabled": {
                  borderColor: "action.disabled",
                  color: "text.disabled",
                },
              }}
            >
              {isCountdownActive ? (
                <>
                  Reenviar en {countdown}s
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                </>
              ) : (
                "Reenviar código"
              )}
            </SendButton>
          )}
        </CustomDialogActions>
      </CustomDialog>

      <Box // Contenedor de página completa con fondo animado CLARO
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          // Fondo con gradiente y animación sutil para tema CLARO
          background: `linear-gradient(135deg, ${customThemeColors.BACKGROUND_START} 0%, ${customThemeColors.BACKGROUND_END} 100%)`,
          animation: "light-gradient-shift 20s ease infinite",
          "@keyframes light-gradient-shift": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
          backgroundSize: "200% 200%",
        }}
      >
        <Container maxWidth="sm">
          {/* Animación de entrada usando framer-motion */}
          <Fade in timeout={600}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, type: "spring", stiffness: 100, delay: 0.1 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: theme.spacing(3),
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  width: "100%",
                  padding: { xs: theme.spacing(3), sm: theme.spacing(5) },
                  borderRadius: "20px",
                  // Sombra basada en el Morado
                  boxShadow: `0px 10px 40px ${hexToRgba(customThemeColors.ACCENT_SECONDARY, 0.3)}`,
                  // CAMBIO: Fondo BLANCO para la tarjeta
                  backgroundColor: customThemeColors.CONTAINER_BG,
                  // Borde de énfasis
                  border: `2px solid ${hexToRgba(customThemeColors.ACCENT_SECONDARY, 0.2)}`,
                  transition: "all 0.3s ease-in-out",
                  // CAMBIO: Color de texto principal OSCURO
                  color: customThemeColors.TEXT_PRIMARY,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: theme.spacing(1.5),
                    mb: theme.spacing(3),
                  }}
                >
                  <img
                    src={LogoLogo}
                    alt="Logo"
                    style={{
                      width: "240px",
                      height: "80px",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  align="center"
                  gutterBottom
                  sx={{
                    // CAMBIO: Color de título principal OSCURO
                    color: customThemeColors.TEXT_PRIMARY,
                    fontWeight: "bold",
                    mb: theme.spacing(4),
                  }}
                >
                  Crea tu Cuenta
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignContent: "center",
                    mb: 1,
                  }}
                >
                  <LoginGoogle generateTokenEatery={generateTokenEatery} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, width: '100%' }}>
                  <Box sx={{ flexGrow: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                  <Typography sx={{ mx: 2, color: '#9e9e9e', fontSize: '0.85rem', fontWeight: 500 }}>
                    o
                  </Typography>
                  <Box sx={{ flexGrow: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                </Box>

                <FormProvider {...methods}>
                  <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Controller
                          name="name"
                          control={methods.control}
                          defaultValue=""
                          rules={{ required: "Nombre es requerido" }}
                          render={({ field, fieldState }) => (
                            <NoSuggestionsTextField
                              {...field}
                              fullWidth
                              label="Nombre"
                              variant="outlined"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              // Asume que NoSuggestionsTextField ahora maneja estilos para tema claro/inputs
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name="last_name"
                          control={methods.control}
                          defaultValue=""
                          rules={{
                            required: "El apellido es requerido.",
                          }}
                          render={({ field, fieldState }) => (
                            <NoSuggestionsTextField
                              {...field}
                              fullWidth
                              label="Apellido"
                              variant="outlined"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name="email"
                          control={methods.control}
                          defaultValue=""
                          rules={{
                            required: "Email es requerido",
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: "Correo inválido.",
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <NoSuggestionsTextField
                              {...field}
                              fullWidth
                              label="Correo"
                              variant="outlined"
                              type="email"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 3 }}>
                        <Box display="flex" justifyContent="center">
                          <SendButton
                            component={motion.button}
                            whileHover={{ scale: 1.05, boxShadow: `0 5px 15px ${hexToRgba(customThemeColors.ACCENT_PRIMARY, 0.4)}` }}
                            whileTap={{ scale: 0.95 }}
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={sendButtonStyles}
                          >
                            {loading ? (
                              <CircularProgress size={24} sx={{ color: customThemeColors.CONTAINER_BG }} />
                            ) : (
                              "Crear Cuenta"
                            )}
                          </SendButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </FormProvider>
                
                {/* Enlace para volver al login */}
                <Typography
                  align="center"
                  sx={{
                    mt: theme.spacing(3),
                    color: customThemeColors.TEXT_SECONDARY, // Texto gris secundario
                  }}
                >
                  ¿Ya tienes cuenta? 
                  <a
                    href="/login"
                    style={{
                      color: customThemeColors.ACCENT_SECONDARY, // Morado para el enlace
                      textDecoration: "none",
                      fontWeight: "bold",
                      marginLeft: theme.spacing(0.5),
                      transition: "color 0.2s ease-in-out",
                      "&:hover": {
                         color: customThemeColors.ACCENT_PRIMARY, // Rojo al hacer hover en el enlace
                      }
                    }}
                  >
                    Inicia Sesión
                  </a>
                </Typography>
                
              </Paper>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled" // Mantenido filled para fuerte contraste en el snackbar
          sx={{
            width: "100%",
            color: customThemeColors.CONTAINER_BG, // Texto del snackbar blanco
            backgroundColor: snackbarSeverity === "success"
              ? customThemeColors.success
              : customThemeColors.error,
            borderRadius: "8px",
            ".MuiAlert-icon": {
              color: customThemeColors.CONTAINER_BG,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Register;